/**
 * Pure helpers of the target correction grid (masterplan skpi-redesign C3a; E5, E11; §2 rules 7, 13).
 *
 * The grid shows one level (persons, teams, companies, the business unit) and exactly one KPI family. What a
 * level shows is the logic of the former PersonGrid / GroupGrid of TargetsTab, cut by level and family:
 *   - a KPI takes a stored target at a level when its `TargetGrain` has that level's bit (PERSON 1, TEAM 2,
 *     COMPANY 4, BU 8 — BAB is 12, company and BU only);
 *   - a target the server calculates (`DerivedTarget`, K2: C visits = Total − A − B) is shown read-only beside
 *     the stored ones; its value is always the server's (GET targets, the `derived` block of a PUT) — the page
 *     never computes it, it only judges the value it was given (negative = Total below A + B);
 *   - a group cell (team, company, BU) may carry the engine's Σ of the person targets as a second line.
 * E11: a dot marks a target that differs from the prior year's target of the same key; the prior year's target
 * stands small under the value. No audit comparison.
 * Review Phase C (C3c): the Σ line runs over all rows of the selection, not the searched ones (`targetGridView`); a
 * sort by a column that is gone falls back to the name (`effectiveSortKey`); a stored change merges only into the
 * year it was written for (`mergeStoredTarget`).
 */
import type { KpiDefinition, KpiPersonYear, KpiTargetRow, ScopeType } from '../types/strategicKpis';
import type { SkpiFamily, TargetLevel } from './strategicKpiUrl';

/** The scope type a level writes (PUT targets `scopeType`). */
export const LEVEL_SCOPE: Record<TargetLevel, ScopeType> = { person: 'PERSON', team: 'TEAM', company: 'COMPANY', bu: 'BU' };

/** `KpiDefinition.TargetGrain` bits, as in TargetsTab and the API. */
export const SCOPE_BIT: Record<ScopeType, number> = { PERSON: 1, TEAM: 2, COMPANY: 4, BU: 8 };

/** The level segment (plural) and the first column's header (singular). */
export const LEVEL_LABEL: Record<TargetLevel, string> = { person: 'Persons', team: 'Teams', company: 'Companies', bu: 'Business unit' };
export const LEVEL_ROW_LABEL: Record<TargetLevel, string> = { person: 'Person', team: 'Team', company: 'Company', bu: 'Business unit' };

/** The key of the targets map — `tkey` of TargetsTab. */
export const targetKey = (kpiKey: string, scopeType: ScopeType, scopeKey: string): string => `${kpiKey}|${scopeType}|${scopeKey}`;

type GrainKpi = Pick<KpiDefinition, 'KpiKey' | 'Category' | 'TargetGrain' | 'IsActive' | 'SortOrder' | 'DerivedTarget'>;

/** Grain lock: does the KPI take a stored target at this level? */
export function takesTargetAt(kpi: Pick<KpiDefinition, 'TargetGrain'>, level: TargetLevel): boolean {
    return (kpi.TargetGrain & SCOPE_BIT[LEVEL_SCOPE[level]]) !== 0;
}

const isDerived = (k: Pick<KpiDefinition, 'DerivedTarget'>) => !!k.DerivedTarget;

/** The active KPIs of one family, in catalogue order (TargetsTab `activeKpis`). */
function familyKpis<K extends GrainKpi>(defs: readonly K[], family: SkpiFamily): K[] {
    return defs
        .filter(d => d.IsActive && d.Category === family)
        .sort((a, b) => a.SortOrder - b.SortOrder || a.KpiKey.localeCompare(b.KpiKey));
}

/**
 * The columns of the grid: the family's active KPIs that take a target at this level, plus the derived ones
 * (C visits) — but a derived column only beside at least one stored column, never alone.
 */
export function gridColumns<K extends GrainKpi>(defs: readonly K[], family: SkpiFamily, level: TargetLevel): K[] {
    const all = familyKpis(defs, family);
    const stored = all.filter(k => !isDerived(k) && takesTargetAt(k, level));
    if (stored.length === 0) return [];
    return all.filter(k => isDerived(k) || takesTargetAt(k, level));
}

/** The number on the family segment: the family's KPIs that take a target at some level, the derived one included. */
export function familyTargetCount(defs: readonly GrainKpi[], family: SkpiFamily): number {
    return familyKpis(defs, family).filter(k => isDerived(k) || k.TargetGrain !== 0).length;
}

/**
 * E5 grain lock of a whole family: the family takes targets, but none at this level (BAB on persons and teams).
 * Returns the levels where it does take targets, in level order; null when the family is not locked here (it has
 * columns at this level, or it takes no target anywhere — that is an empty family, not a lock).
 */
export function familyLock(defs: readonly GrainKpi[], family: SkpiFamily, level: TargetLevel, levels: readonly TargetLevel[]): TargetLevel[] | null {
    if (gridColumns(defs, family, level).length > 0) return null;
    const open = levels.filter(l => gridColumns(defs, family, l).length > 0);
    return open.length > 0 ? open : null;
}

/** The text of the lock: "BAB targets exist for companies and the business unit only." */
export function familyLockText(familyLabel: string, open: readonly TargetLevel[]): string {
    const names = open.map(l => (l === 'bu' ? 'the business unit' : LEVEL_LABEL[l].toLowerCase()));
    const list = names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    return `${familyLabel} targets exist for ${list} only.`;
}

/** TargetCell's own tolerance for "the same value" (it does not store a change below it). */
export const SAME_VALUE_EPS = 0.00005;

/** E11: does this target differ from the prior year's target of the same key? Set ↔ unset counts as a difference. */
export function differsFromPrior(current: number | null | undefined, prior: number | null | undefined): boolean {
    const c = current ?? null;
    const p = prior ?? null;
    if (c === null && p === null) return false;
    if (c === null || p === null) return true;
    return Math.abs(c - p) >= SAME_VALUE_EPS;
}

/**
 * Whether the grid marks differences at all: only against a prior year that has stored targets. Against an empty
 * prior year every target would carry a dot, which says nothing.
 */
export function priorYearHasTargets(prior: ReadonlyMap<string, Pick<KpiTargetRow, 'Derived'>> | null | undefined): boolean {
    if (!prior) return false;
    for (const r of prior.values()) if (!r.Derived) return true;
    return false;
}

/** The E11 dot of one stored cell. Derived cells never carry one (they follow from Total, A and B). */
export function showsDeviationDot(current: number | null | undefined, prior: number | null | undefined, priorHasTargets: boolean): boolean {
    return priorHasTargets && differsFromPrior(current, prior);
}

/** A derived target (C visits) as the cell shows it: none, a value, or a negative value with a warning. */
export type DerivedTargetState =
    | { kind: 'none' }
    | { kind: 'value'; value: number }
    | { kind: 'negative'; value: number };

/** Judges the server's derived value; never computes it (K2). */
export function derivedTargetState(value: number | null | undefined): DerivedTargetState {
    if (value === null || value === undefined || !Number.isFinite(value)) return { kind: 'none' };
    // round4 on the server: anything below −0.00005 is a real negative, not a rounding remainder.
    return value <= -SAME_VALUE_EPS ? { kind: 'negative', value } : { kind: 'value', value };
}

/** One row of the grid: a person or a group node. */
export interface TargetGridRow {
    scopeType: ScopeType;
    scopeKey: string;
    label: string;
    /** The second line: team · company for a person; "n persons · …" for a group. */
    detail: string;
    /** Persons of the node in the selection (1 for a person). */
    personCount: number;
    /** A person who has left (KpiPersonYear.IsActive = false). */
    inactive?: boolean;
    /** Hover text of the name (StaffID and note for a person). */
    title?: string;
}

/** "5 persons", or with a filter that cuts the node "2 of 4 persons selected" (TargetsTab `countText`). */
export const countText = (shown: number, all: number): string => (shown < all ? `${shown} of ${all} persons selected` : `${shown} persons`);

const byLabel = (a: TargetGridRow, b: TargetGridRow) => a.label.localeCompare(b.label, 'de-CH');

/**
 * The rows of one level (TargetsTab `groups` / PersonGrid, one level at a time).
 * `inScope` = the persons of the BUs that the selection leaves; `inBus` = all persons of the BUs (for "n of m").
 */
export function targetGridRows(level: TargetLevel, inScope: readonly KpiPersonYear[], inBus: readonly KpiPersonYear[], bus: readonly string[], nameOf: ReadonlyMap<string, string>): TargetGridRow[] {
    if (level === 'person') {
        return inScope.map(p => {
            const team = p.TeamManagerStaffID ? nameOf.get(p.TeamManagerStaffID) ?? p.TeamManagerStaffID : null;
            return {
                scopeType: 'PERSON' as const,
                scopeKey: p.StaffID,
                label: p.FullName,
                detail: team ? `${team} · ${p.CompanyCode}` : p.CompanyCode,
                personCount: 1,
                inactive: !p.IsActive,
                title: `${p.StaffID}${p.Note ? ' · ' + p.Note : ''}`,
            };
        }).sort(byLabel);
    }
    if (level === 'bu') {
        const out: TargetGridRow[] = [];
        for (const bu of bus) {
            const n = inScope.filter(p => p.BUCode === bu).length;
            if (n > 0) out.push({ scopeType: 'BU', scopeKey: bu, label: bu, detail: countText(n, inBus.filter(p => p.BUCode === bu).length), personCount: n });
        }
        return out;
    }
    if (level === 'company') {
        return [...new Set(inScope.map(p => p.CompanyCode))].sort().map(c => {
            const members = inScope.filter(p => p.CompanyCode === c);
            const buList = [...new Set(members.map(p => p.BUCode))].join(', ');
            return { scopeType: 'COMPANY' as const, scopeKey: c, label: c, detail: `${countText(members.length, inBus.filter(p => p.CompanyCode === c).length)} · ${buList}`, personCount: members.length };
        });
    }
    const teams = [...new Set(inScope.map(p => p.TeamManagerStaffID).filter((t): t is string => t !== null))];
    return teams.map(t => {
        const members = inScope.filter(p => p.TeamManagerStaffID === t);
        const companies = [...new Set(members.map(p => p.CompanyCode))].sort().join(', ');
        return { scopeType: 'TEAM' as const, scopeKey: t, label: nameOf.get(t) ?? t, detail: `${countText(members.length, inBus.filter(p => p.TeamManagerStaffID === t).length)} · ${companies}`, personCount: members.length };
    }).sort(byLabel);
}

/** The search box: label or second line contains the text (case-insensitive). */
export function matchesSearch(row: Pick<TargetGridRow, 'label' | 'detail'>, search: string): boolean {
    const q = search.trim().toLowerCase();
    return q === '' || row.label.toLowerCase().includes(q) || row.detail.toLowerCase().includes(q);
}

/** The Σ line of the person level (PersonGrid `sums`): sum, or the mean for pct / days; n = persons with a target. */
export function personColumnSum(values: readonly (number | null | undefined)[], unit: string): { value: number | null; n: number } {
    const vals = values.filter((v): v is number => v !== null && v !== undefined);
    if (vals.length === 0) return { value: null, n: 0 };
    const sum = vals.reduce((s, v) => s + v, 0);
    return { value: unit === 'pct' || unit === 'days' ? sum / vals.length : sum, n: vals.length };
}

/** Sort: by name, or by a KPI's value with the empty cells last (PersonGrid / GroupGrid), ties by name. */
export function sortGridRows(rows: readonly TargetGridRow[], key: string | null, direction: 'asc' | 'desc', valueOf: (row: TargetGridRow, kpiKey: string) => number | null): TargetGridRow[] {
    const dir = direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        if (key === null) return a.label.localeCompare(b.label, 'de-CH') * dir;
        const va = valueOf(a, key), vb = valueOf(b, key);
        if (va === null) return vb === null ? byLabel(a, b) : 1;
        if (vb === null) return -1;
        return (va - vb) * dir || byLabel(a, b);
    });
}

/**
 * The sort key the grid actually uses: a KPI key only while that KPI is a sortable column of the current level and
 * family (stored, not derived). After a level or family switch the column can be gone; the grid then sorts by name
 * (null) instead of by a column nobody sees (Review Phase C, C3c).
 */
export function effectiveSortKey(key: string | null, columns: readonly Pick<KpiDefinition, 'KpiKey' | 'DerivedTarget'>[]): string | null {
    if (key === null) return null;
    return columns.some(c => c.KpiKey === key && !isDerived(c)) ? key : null;
}

/** The rows the grid shows and the Σ line under them. */
export interface TargetGridView {
    /** The rows that match the search, sorted. */
    rows: TargetGridRow[];
    /** The sort in force (see `effectiveSortKey`): a sort by a column that is gone becomes name ascending. */
    sortKey: string | null;
    direction: 'asc' | 'desc';
    /** Person level only: Σ (mean for pct / days) per KPI over ALL rows of the selection, not only the searched ones. */
    sums: Record<string, { value: number | null; n: number }> | null;
    /** The m of «n/m» under the Σ line: all rows of the selection. */
    total: number;
}

/**
 * Search, sort and Σ line in one place. The search only narrows what is shown: «Σ persons» and «n/m» stay over every
 * person of the selection, as in the old PersonGrid (§2 rule 7; Review Phase C, C3c).
 */
export function targetGridView(
    level: TargetLevel,
    allRows: readonly TargetGridRow[],
    columns: readonly Pick<KpiDefinition, 'KpiKey' | 'Unit' | 'DerivedTarget'>[],
    search: string,
    sort: { key: string | null; direction: 'asc' | 'desc' },
    valueOf: (row: TargetGridRow, kpiKey: string) => number | null,
): TargetGridView {
    const sortKey = effectiveSortKey(sort.key, columns);
    const direction = sortKey === sort.key ? sort.direction : 'asc';
    const rows = sortGridRows(allRows.filter(r => matchesSearch(r, search)), sortKey, direction, valueOf);
    const sums = level !== 'person'
        ? null
        : Object.fromEntries(columns.map(k => [k.KpiKey, personColumnSum(allRows.map(r => valueOf(r, k.KpiKey)), k.Unit)]));
    return { rows, sortKey, direction, sums, total: allRows.length };
}

/** What a PUT targets stored: the parts of `PutTargetResult` the tab keeps, plus the year it wrote. */
export interface StoredTargetChange {
    /** The year the PUT wrote. */
    year: number;
    kpiKey: string;
    scopeType: ScopeType;
    scopeKey: string;
    newValue: number | null;
    /** The server's calculated targets of the same scope after the write (C visits). */
    derived?: readonly { kpiKey: string; value: number | null }[];
}

/**
 * Merges a stored change into the tab's targets map, but only while the map still holds the year the PUT wrote. The
 * keys carry no year: a 2026 save that returns after 2027 has loaded would otherwise put the 2026 value into the 2027
 * grid. On a mismatch the map comes back unchanged (same reference); the database already has the value.
 */
export function mergeStoredTarget(
    prev: Map<string, KpiTargetRow>,
    mapYear: number | null,
    change: StoredTargetChange,
    now: string,
): Map<string, KpiTargetRow> {
    if (mapYear !== change.year) return prev;
    const { year, kpiKey, scopeType, scopeKey, newValue } = change;
    const next = new Map(prev);
    for (const d of change.derived ?? []) {
        const dk = targetKey(d.kpiKey, scopeType, scopeKey);
        if (d.value === null) next.delete(dk);
        else next.set(dk, {
            Id: 0, KpiKey: d.kpiKey, TargetYear: year, ScopeType: scopeType, ScopeKey: scopeKey, TargetValue: d.value, Note: null,
            CreatedBy: '', CreatedAt: '', UpdatedBy: '', UpdatedAt: now, Derived: true,
        });
    }
    const k = targetKey(kpiKey, scopeType, scopeKey);
    if (newValue === null) next.delete(k);
    else {
        const old = prev.get(k);
        next.set(k, {
            Id: old?.Id ?? 0, KpiKey: kpiKey, TargetYear: year, ScopeType: scopeType, ScopeKey: scopeKey,
            TargetValue: newValue, Note: old?.Note ?? null,
            CreatedBy: old?.CreatedBy ?? '', CreatedAt: old?.CreatedAt ?? '', UpdatedBy: old?.UpdatedBy ?? '', UpdatedAt: now,
        });
    }
    return next;
}
